import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsAPI, SiteSettings } from '../services/api';

interface SettingsContextProps {
  settings: SiteSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: SiteSettings = {
  clinicName: 'Dynamic Dental Clinic',
  phone: '+977-984-0354608',
  email: 'info@dynamicdentalclinic.com',
  address: 'Beside Yamaha Showroom, Kumaripati, Lalitpur, Nepal',
  googleMapsLink: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3532.5!2d85.3185584!3d27.6716533!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb19001b2b5daf%3A0x8925f470cf5a590!2sDynamic%20Dental!5e0!3m2!1sen!2snp!4v1',
  facebookLink: 'https://facebook.com',
  instagramLink: 'https://instagram.com',
  youtubeLink: 'https://youtube.com',
  linkedinLink: 'https://linkedin.com',
};

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const res = await settingsAPI.get();
      if (res.data) {
        // Merge fetched settings with defaults to ensure all keys are populated
        setSettings({
          clinicName: res.data.clinicName || defaultSettings.clinicName,
          phone: res.data.phone || defaultSettings.phone,
          email: res.data.email || defaultSettings.email,
          address: res.data.address || defaultSettings.address,
          googleMapsLink: res.data.googleMapsLink || defaultSettings.googleMapsLink,
          facebookLink: res.data.facebookLink || defaultSettings.facebookLink,
          instagramLink: res.data.instagramLink || defaultSettings.instagramLink,
          youtubeLink: res.data.youtubeLink || defaultSettings.youtubeLink,
          linkedinLink: res.data.linkedinLink || defaultSettings.linkedinLink,
          appointmentNotificationEmail: res.data.appointmentNotificationEmail,
        });
      }
    } catch (err) {
      console.warn('SettingsProvider: Failed to fetch settings from API, using defaults.', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSettings();
  }, [refreshSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
